import { bearerFetcher, useAuthBearerFetch } from "matsuri-hooks"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { useCallback } from "react"
import { m2mCleaning } from "../endpoints/m2m-cleaning"
import { CleaningInfoDetailType } from "@/model/cleaningInfo"
import { uploadWithRetry } from "@/utils/uploadWithRetry"
import {
    isRetryableUploadError,
    uploadBlobWithPresignedPut,
    uploadWithPresignedUrl
} from "@/utils/presignedUpload"
import { useBackgroundFetchCtx } from "@/hooks/useBackgroundFetch"

export const useCleaningInfoDetail = (
    token: string,
    cleaningId: string | undefined
) => {
    return useAuthBearerFetch<CleaningInfoDetailType>(
        token,
        cleaningId ? m2mCleaning.cleanerV2.getCleaning({ cleaningId }) : null,
        {}
    )
}

export const callRequestStart = async (token: string, cleaningId: string) => {
    const { error } = await bearerFetcher(
        token,
        m2mCleaning.cleanerV2.startCleaning({ cleaningId }),
        {
            method: "POST"
        }
    )

    return { error }
}

export type requestFinishFunc = (
    token: string,
    cleaningId: string
) => Promise<void>
export const callRequestFinish: requestFinishFunc = async (
    token,
    cleaningId
) => {
    await bearerFetcher(
        token,
        m2mCleaning.cleanerV2.finishCleaning({ cleaningId }),
        {
            method: "POST"
        }
    )
}

export const callRequestReport: requestFinishFunc = async (
    token,
    cleaningId
) => {
    await bearerFetcher(
        token,
        m2mCleaning.cleanerV2.reportCleaning({ cleaningId }),
        {
            method: "POST"
        }
    )
}

export interface uploadErrandPictureObject {
    errandId: string
    blob: Blob
}
export type uploadErrandPictureFunc = (
    input: uploadErrandPictureObject
) => Promise<void>

export const useUploadErrandPicture = () => {
    const { token } = useAuth()
    const { Fetch } = useBackgroundFetchCtx()
    return useCallback(
        async ({ errandId, blob }: uploadErrandPictureObject) => {
            const issueUrl = m2mCleaning.cleanerV2.issueErrandPictureUploadUrl({
                errandId
            })
            const completeUrl =
                m2mCleaning.cleanerV2.completeErrandPictureUpload({ errandId })
            await uploadWithPresignedUrl({
                token,
                issueUrl,
                completeUrl,
                blob,
                expectJsonResponse: false,
                // 完了通知の失敗はバックグラウンドキューに退避して後で再送する。
                // S3 PUT までは成功しているので complete だけ確実に送り直せれば良い。
                sendComplete: async (url, body) => {
                    try {
                        const result = await bearerFetcher(token ?? "", url, {
                            method: "POST",
                            body
                        })
                        if (result.error) {
                            throw Object.assign(new Error(`complete failed`), {
                                status: result.error.status
                            })
                        }
                        return result.data
                    } catch (e) {
                        if (!isRetryableUploadError(e)) {
                            throw e
                        }
                        try {
                            await Fetch(url, {
                                method: "POST",
                                headers: token
                                    ? { Authorization: `Bearer ${token}` }
                                    : {},
                                body
                            })
                        } catch (queueError) {
                            console.error(
                                "[useUploadErrandPicture] BackgroundFetch enqueue failed",
                                queueError
                            )
                            throw queueError
                        }
                        return undefined
                    }
                }
            })
        },
        [Fetch, token]
    )
}

export interface requestCreateTroubleObject {
    description: string
    pictureData?: string
    pictures?: string[]
}
export type requestCreateTroubleFunc = (
    input: requestCreateTroubleObject
) => Promise<void>
export const callRequestCreateTrouble = async (
    token: string,
    cleaningId: string,
    input: requestCreateTroubleObject
) => {
    const body = JSON.stringify({
        description: input.description,
        pictureData: input.pictureData,
        pictures: input.pictures
    })

    try {
        const result = await uploadWithRetry(async () => {
            const r = await bearerFetcher<{ id: string }>(
                token,
                m2mCleaning.cleanerV2.sendProblems({
                    cleaningId
                }),
                {
                    method: "POST",
                    body
                }
            )
            if (r.error) throw r.error
            return r
        })

        return result
    } catch (error) {
        console.error("[callRequestCreateTrouble] API呼び出しエラー", {
            cleaningId,
            error,
            errorMessage:
                error instanceof Error ? error.message : String(error),
            errorStack: error instanceof Error ? error.stack : undefined
        })

        // エラーの詳細情報を保持したまま返す
        return {
            data: undefined,
            error:
                error instanceof Error
                    ? error
                    : new Error(
                          `トラブル報告のAPI呼び出しに失敗しました: ${String(error)}`
                      )
        }
    }
}
export const useRequestCreateTrouble = (cleaningId: string) => {
    const { token } = useAuth()
    return useCallback(
        (input: requestCreateTroubleObject) =>
            callRequestCreateTrouble(token, cleaningId, input),
        [cleaningId, token]
    )
}

// Presigned URL 方式のトラブル報告。
// 各 Blob を issue→S3 PUT で先にアップロードし、得られた uploadIds と description を
// complete エンドポイント (createTroubleWithUploadedPictures) に渡す。
// 並列度は uploadBlobWithPresignedPut の throttle に依存しない素直な Promise.all。
export const callRequestCreateTroubleWithUploadedPictures = async (
    token: string,
    cleaningId: string,
    input: { description: string; blobs: Blob[] }
): Promise<{ data?: { id: string }; error?: Error }> => {
    try {
        const uploadIds = await Promise.all(
            input.blobs.map(async blob => {
                const issueUrl =
                    m2mCleaning.cleanerV2.issueTroublePictureUploadUrl({
                        cleaningId
                    })
                const { uploadId } = await uploadBlobWithPresignedPut({
                    token,
                    issueUrl,
                    blob
                })
                return uploadId
            })
        )

        const result = await bearerFetcher<{ id: string }>(
            token,
            m2mCleaning.cleanerV2.createTroubleWithUploadedPictures({
                cleaningId
            }),
            {
                method: "POST",
                body: JSON.stringify({
                    description: input.description,
                    uploadIds
                })
            }
        )
        if (result.error) {
            return { data: undefined, error: new Error(result.error.message) }
        }
        if (!result.data?.id) {
            return {
                data: undefined,
                error: new Error("id missing in complete response")
            }
        }
        return { data: result.data, error: undefined }
    } catch (error) {
        console.error(
            "[callRequestCreateTroubleWithUploadedPictures] API呼び出しエラー",
            {
                cleaningId,
                error,
                errorMessage:
                    error instanceof Error ? error.message : String(error),
                errorStack: error instanceof Error ? error.stack : undefined
            }
        )
        return {
            data: undefined,
            error:
                error instanceof Error
                    ? error
                    : new Error(
                          `トラブル報告のAPI呼び出しに失敗しました: ${String(error)}`
                      )
        }
    }
}
